import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, forkJoin, Subject, Subscription, Observable } from 'rxjs';
import {
  catchError,
  finalize,
  takeUntil,
  tap,
  map,
  filter,
  switchMap,
} from 'rxjs/operators';
import { NgbModalRef, NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  FormState,
  ConfirmModalComponent,
  removeItemFromArray,
  RoutingState,
  UpdateRecentRoutes,
} from '@shared';

import { UserService } from '@account-users/user/services/user.service';
import { IAccount } from '@account-users/account/models/IAccount';
import { AuthService } from '@account-users/auth/auth.service';
import { User } from '@account-users/user/models/User';
import { AccountUserType } from '@account-users/account/models/AccountUserType';
import { LoadPermissions } from '@account-users/user/state/permissions/permissions.actions';
import { Store, select } from '@ngrx/store';
import { PermissionsState } from '@account-users/user/state/permissions/permissions.reducer';
import * as fromUser from '@account-users/user/state';

import { Permission } from '../../../account/models/Permission';
import { LoadRoles } from '../../../user/state/roles/roles.actions';
import { IUserRole } from '../../../user/models/UserRole';
import { Title } from '@angular/platform-browser';
import { IToolInfo } from '../../../account/models/IToolInfo';
import { AccountCategory } from '../../../account/models/AccountCategory';
import { AccountService } from '../../../account/services/account.service';
import { RequestInvitationLink } from '@account-users/user/state/user/user.actions';

import { hasPermission } from '@account-users/user/state';
import { UserState } from '@account-users/user/state/user/user.reducer';
import { UserRoles } from '@account-users/account/models';

interface AccountToolInfo {
  accountName: string;
  toolPermissions: IToolInfo[];
}

@Component({
  selector: 'app-user-page',
  templateUrl: './user-page.component.html',
  styleUrls: ['./user-page.component.scss'],
})
export class UserPageComponent implements OnInit, OnDestroy {
  user: User;
  userPermissions$: Observable<Permission[]>;
  userRoles$: Observable<IUserRole[]>;
  loggedInUserHasInvitationLinkPermission$: Observable<boolean>;

  loggedInUser: User;
  accounts: IAccount[] = [];
  loading = false;
  isUpdatingRole = false;
  userId = '';
  isUnblockingUser = false;
  hasMfaEnabled: boolean;
  formState = FormState;
  accountToolInfo: AccountToolInfo[];
  blockUserLoading: boolean;
  blockUserError: boolean;
  accountCategories: AccountCategory[];
  blockUserModalRef: NgbModalRef;
  selectedUserIsInternalApsisUser: boolean;

  private sub: Subscription;
  private removeMfaModalRef: NgbModalRef;

  private destroy$: Subject<void> = new Subject<void>();

  @ViewChild('blockUserBodyTemplate', { static: true })
  blockUserBodyTemplateRef: TemplateRef<any>;

  constructor(
    private readonly userService: UserService,
    private readonly accountService: AccountService,
    private readonly route: ActivatedRoute,
    private readonly toastr: ToastrService,
    private readonly auth: AuthService,
    private readonly modalService: NgbModal,
    private store: Store<PermissionsState | UserState>,
    private routingStore: Store<RoutingState>,
    private pageTitle: Title,
  ) {}

  ngOnInit() {
    this.sub = this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.userId = params['userId'];

        this.loading = true;
        forkJoin([
          this.userService.fetchUser(this.userId),
          this.userService.fetchUser(this.auth.profile.userId),
          this.userService.fetchAllowedAccounts(this.userId),
          this.accountService.fetchAccountCategories(),
        ])
          .pipe(
            finalize(() => {
              this.loading = false;
            }),
            takeUntil(this.destroy$),
            catchError((error) => {
              this.toastr.error(
                'Failed to fetch user details',
                'Request failed',
                { closeButton: true, disableTimeOut: true },
              );

              return EMPTY;
            }),
          )
          .subscribe(
            ([user, loggedInUser, allowedAccounts, accountCategories]) => {
              this.user = user['user'];
              this.loggedInUser = loggedInUser['user'];
              this.accounts = allowedAccounts['accounts'];
              this.pageTitle.setTitle(`${this.user.name} | One Back Office`);
              this.accountCategories = accountCategories;
              this.selectedUserIsInternalApsisUser = this.isSelectedUserInternalApsisUser();

              this.routingStore.dispatch(
                new UpdateRecentRoutes({
                  id: 0,
                  type: 'User',
                  title: `${this.user.name}`,
                  url: location.href,
                }),
              );
            },
          );
      });
    this.store.dispatch(new LoadRoles());
    this.userRoles$ = this.store.pipe(select(fromUser.getUserRoles));

    this.store.dispatch(new LoadPermissions());
    this.userPermissions$ = this.store.pipe(
      select(fromUser.getUserPermissions),
    );
    this.loggedInUserHasInvitationLinkPermission$ = this.store.pipe(
      select(hasPermission(Permission.CanRequestInvitationLink)),
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.destroy$.next();
    this.toastr.clear();
  }

  get customerAccounts() {
    return this.accounts.filter(
      (account) => account.accountUserType === AccountUserType.Customer,
    );
  }

  get staffUserAccounts() {
    return this.accounts.filter(
      (account) => account.accountUserType === AccountUserType.Staff,
    );
  }

  get isCurrentUser() {
    return this.auth.profile && this.auth.profile.email === this.user.email;
  }

  get canUserBeBlocked() {
    return !this.user.roles.some((role) =>
      [UserRoles.Owner, UserRoles.OrderManager].includes(role),
    );
  }

  get canActingUserBlock() {
    return (this.loggedInUser.roles || []).some((role) =>
      [UserRoles.Owner, UserRoles.OrderManager].includes(role),
    );
  }

  updateUserRoles(role: string) {
    this.isUpdatingRole = true;
    const addNewRole = !this.user.roles.includes(role as UserRoles);
    if (addNewRole) {
      this.userService
        .putUserRole(role, this.userId)
        .pipe(
          catchError((error) => {
            console.error(error);
            this.toastr.error('Could not update role', 'Request failed');
            return EMPTY;
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.user.roles = [...this.user.roles, role as UserRoles];
          this.isUpdatingRole = false;
        });
    } else {
      this.userService
        .deleteUserRole(role, this.userId)
        .pipe(
          catchError((error) => {
            console.error(error);
            this.toastr.error('Could not update role', 'Request failed');
            return EMPTY;
          }),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.user.roles = removeItemFromArray(this.user.roles, role);
          this.isUpdatingRole = false;
        });
    }
  }

  deleteBlockedByLogin(user: User) {
    this.isUnblockingUser = true;

    this.userService
      .deleteBlockedByLogin(user)
      .pipe(
        tap(() => {
          this.user = {
            ...this.user,
            blockedByLogin: false,
          };
          this.toastr.success('User has been unblocked!', 'Success');
        }),
        finalize(() => {
          this.isUnblockingUser = false;
        }),
        catchError((error) => {
          console.error(error);
          this.toastr.error('Could not unblock user.', 'Request failed');
          return EMPTY;
        }),

        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  removeMfaVerify() {
    this.removeMfaModalRef = this.modalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    const { componentInstance } = this.removeMfaModalRef;
    componentInstance.headerText = `Confirm Remove MFA`;
    componentInstance.bodyText = `
    <div>
      You are about to remove the Multifactor Authentication:
    </div>
    <div><br>
      <b>User:</b>  ${this.user.name} <br>
      <b>Type:</b>  ${
        this.user.use_mfa ? 'SMS MFA' : 'Google Authenticator MFA'
      }
    </div>
    <div><br>
    Have you documented how you securely identified the customer?
    </div>`;
    componentInstance.confirmButtonText = 'Confirm';
    componentInstance.confirmationResult.subscribe((hasVerified: boolean) => {
      if (hasVerified) {
        componentInstance.state = FormState.submitting;
        this.userService
          .deleteMFAForUser(this.user)
          .pipe(
            takeUntil(this.destroy$),
            catchError((error) => {
              console.error(error);
              componentInstance.state = FormState.failure;
              this.toastr.error(
                'Could not disable MFA for this user.',
                'Request failed',
              );
              this.removeMfaModalRef.close();
              return EMPTY;
            }),
          )
          .subscribe((_) => {
            this.user = {
              ...this.user,
              use_google: false,
              use_mfa: false,
            };
            componentInstance.state = FormState.success;
            this.toastr.success('MFA for user has been removed!', 'Success');
            this.removeMfaModalRef.close();
          });
      }
    });
  }

  blockUser() {
    this.accountToolInfo = [];
    this.blockUserLoading = true;
    this.blockUserError = false;

    this.blockUserModalRef = this.modalService.open(ConfirmModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    const componentInstance = this.blockUserModalRef
      .componentInstance as ConfirmModalComponent;
    componentInstance.state = FormState.submitting;
    componentInstance.headerText = 'Confirm block user';
    componentInstance.bodyTemplate = this.blockUserBodyTemplateRef;
    componentInstance.confirmButtonText = 'Yes';
    componentInstance.confirmationResult
      .pipe(
        filter((response) => response),
        tap(() => (componentInstance.state = FormState.submitting)),
        switchMap(() =>
          this.userService.blockUser(this.userId).pipe(
            takeUntil(this.destroy$),
            catchError(() => {
              this.blockUserError = true;
              componentInstance.state = FormState.failure;
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe((_) => {
        componentInstance.state = FormState.success;
        this.user.blocked = true;
        this.blockUserModalRef.close();
      });

    forkJoin([
      ...(this.customerAccounts || []).map((account) =>
        this.userService.getUserDetails(account.id, this.userId).pipe(
          map((response) => ({
            accountName: account.displayName,
            toolPermissions: [],
          })),
        ),
      ),
      ...(this.staffUserAccounts || []).map((account) =>
        this.userService.getUserDetails(account.id, this.userId).pipe(
          map((response) => ({
            accountName: account.displayName,
            toolPermissions: [],
          })),
        ),
      ),
    ])
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          componentInstance.state = FormState.success;
          this.blockUserLoading = false;
        }),
      )
      .subscribe((accountToolInfo) => (this.accountToolInfo = accountToolInfo));
  }

  requestInvitationLink() {
    this.store.dispatch(new RequestInvitationLink(this.userId));
  }

  private isSelectedUserInternalApsisUser() {
    return (
      this.user.email &&
      (this.user.email.includes('@apsis.com') ||
        this.user.email.includes('@efficy.com'))
    );
  }
}
